import { isNormalPiece, isDeadPiece, turnAlivePieceDead, isSuperPowerUp, isBarrelTile, damageBarrel } from './MferHead';
import { GameBoard, StateChange, MatchResult, FillResult, PowerUpResult, GravityResult, GravityMovement } from '../../types/games/mfersbeheaded';
import { Position } from '../../types/common';

/**
 * Finds horizontal matches for a given position on the board
 */
export const horizontalMatchPositions = (
  board: GameBoard, 
  row: number, 
  col: number, 
  boardSize: number
): MatchResult => {
  const jewelType = board[row][col];
  const matches: Position[] = [];

  let maxMatch = -1;
  let minMatch = boardSize;
  
  // Check right side from current position
  for (let i = col; i < boardSize; i++) {
    if (board[row][i] === jewelType && isNormalPiece(board[row][i])) {
      matches.push({row, col: i});
      maxMatch = i > maxMatch ? i : maxMatch;
      minMatch = i < minMatch ? i : minMatch;
    } else {
      break;
    }
  }

  // Check left side from current position
  for (let i = col - 1; i >= 0; i--) {
    if (board[row][i] === jewelType && isNormalPiece(board[row][i])) {
      matches.push({row, col: i});
      maxMatch = i > maxMatch ? i : maxMatch;
      minMatch = i < minMatch ? i : minMatch;
    } else {
      break;
    }
  }
  
  return matches.length >= 3 ? [matches, col === Math.floor((minMatch + maxMatch) / 2)] : [[], false];
};

/**
 * Finds vertical matches for a given position on the board
 */
export const verticalMatchPositions = (
  board: GameBoard, 
  row: number, 
  col: number, 
  boardSize: number
): MatchResult => {
  const jewelType = board[row][col];
  const matches: Position[] = [];
  let maxMatch = -1;
  let minMatch = boardSize;
  
  // Check down from current position
  for (let i = row; i < boardSize; i++) {
    if (board[i][col] === jewelType && isNormalPiece(board[i][col])) {
      matches.push({row: i, col});
      maxMatch = i > maxMatch ? i : maxMatch;
      minMatch = i < minMatch ? i : minMatch;
    } else {
      break;
    } 
  }

  // Check up from current position
  for (let i = row - 1; i >= 0; i--) {
    if (board[i][col] === jewelType && isNormalPiece(board[i][col])) {
      matches.push({row: i, col});
      maxMatch = i > maxMatch ? i : maxMatch;
      minMatch = i < minMatch ? i : minMatch;
    } else {
      break;
    }
  }
  
  return matches.length >= 3 ? [matches, row === Math.floor((minMatch + maxMatch) / 2)] : [[], false];
};

/**
 * Sanitizes a username by decoding and checking length
 */
export const sanitizeUserName = (userName: string | null): string | null => {
  if (!userName) return null;
  
  const decodedName = decodeURIComponent(userName);
  
  if (decodedName.length > 20) return null;
  
  // You can add other sanitization checks here if needed.
  return decodedName;
};

/**
 * Fills dead pieces on the board with new jewels
 */
export const fillWithNewJewels = (
  board: GameBoard, 
  boardSize: number, 
  numJewelTypes: number = 6
): FillResult => {
  const jewelTypes = Array.from({length: numJewelTypes}, (_, i) => i + 1);  // [1, 2, 3, 4, 5, 6]
  const stateChanges: StateChange[] = [];
  let nFilled = 0;
  
  for (let col = 0; col < boardSize; col++) {
    for (let row = 0; row < boardSize; row++) {
      if (isDeadPiece(board[row][col])) {
        board[row][col] = jewelTypes[Math.floor(Math.random() * numJewelTypes)];
        stateChanges.push([row, col, board[row][col]]);
        nFilled += 1;
      }
    }
  }
  
  return [nFilled, stateChanges];
};

/**
 * Returns the difference between two sets (elements in setA but not in setB)
 */
export const setDifference = <T>(setA: Set<T> | T[], setB: Set<T> | T[]): Set<T> => {
  const difference = new Set(setA);
  
  // Convert setB to array if it's a Set to handle iteration
  const setBArray = Array.isArray(setB) ? setB : Array.from(setB);
  
  for (const elem of setBArray) {
    difference.delete(elem);
  }
  return difference;
};

/**
 * Applies gravity to the board by making pieces fall down to fill empty spaces
 */
export const applyGravity = (board: GameBoard, boardSize: number): StateChange[] => {
  const stateChanges: StateChange[] = [];
  for (let col = 0; col < boardSize; col++) {
    let emptyRow = (boardSize - 1);  // Start from the bottom
    for (let row = (boardSize - 1); row >= 0; row--) {
      if (isDeadPiece(board[row][col])) {
        let nonEmptyRowAbove = row - 1;
        while (nonEmptyRowAbove >= 0 && isDeadPiece(board[nonEmptyRowAbove][col])) {
          nonEmptyRowAbove--;
        }

        if (nonEmptyRowAbove < 0) { // No more non-empty jewels in this column
          break;
        }

        board[emptyRow][col] = board[nonEmptyRowAbove][col];
        stateChanges.push([emptyRow, col, board[emptyRow][col]]);
        // if not already dead, set it to dead version
        if (!isDeadPiece(board[nonEmptyRowAbove][col])) {
          board[nonEmptyRowAbove][col] = turnAlivePieceDead(board[nonEmptyRowAbove][col]);
          stateChanges.push([nonEmptyRowAbove, col, board[nonEmptyRowAbove][col]]);
        }

        emptyRow--;
      } else {
        emptyRow--;
      }
    }
  }
  return stateChanges;
};

/**
 * NEW: Enhanced version of applyGravity that returns detailed movement information
 * for replay recording
 */
export const applyGravityEnhanced = (board: GameBoard, boardSize: number): GravityResult => {
  const stateChanges: StateChange[] = [];
  const movements: GravityMovement[] = [];
  const clearedCells: StateChange[] = [];

  for (let col = 0; col < boardSize; col++) {
    let emptyRow = (boardSize - 1);  // Start from the bottom
    for (let row = (boardSize - 1); row >= 0; row--) {
      if (isDeadPiece(board[row][col])) {
        let nonEmptyRowAbove = row - 1;
        while (nonEmptyRowAbove >= 0 && isDeadPiece(board[nonEmptyRowAbove][col])) {
          nonEmptyRowAbove--;
        }

        if (nonEmptyRowAbove < 0) { // No more non-empty jewels in this column
          break;
        }

        const pieceValue = board[nonEmptyRowAbove][col];

        // Record the movement
        movements.push({
          from: [nonEmptyRowAbove, col],
          to: [emptyRow, col],
          value: pieceValue
        });

        // Move the piece
        board[emptyRow][col] = pieceValue;
        stateChanges.push([emptyRow, col, board[emptyRow][col]]);

        // Mark source as dead
        if (!isDeadPiece(pieceValue)) {
          const deadValue = turnAlivePieceDead(pieceValue);
          board[nonEmptyRowAbove][col] = deadValue;
          stateChanges.push([nonEmptyRowAbove, col, deadValue]);
          clearedCells.push([nonEmptyRowAbove, col, pieceValue]); // Record what was there
        }

        emptyRow--;
      } else {
        emptyRow--;
      }
    }
  }

  return { stateChanges, movements, clearedCells };
};

/**
 * Applies super power-up effect by clearing entire row and column
 */
export const applySuperPowerUp = (
  board: GameBoard, 
  rowIndex: number, 
  colIndex: number, 
  boardSize: number
): PowerUpResult => {
  const stateChanges: StateChange[] = [];
  if (!isSuperPowerUp(board[rowIndex][colIndex])) {
    return [0, stateChanges];
  }

  let deadCount = 0;

  // Mark cells in the same column as dead or damage barrels
  for (let r = 0; r < boardSize; r++) {
    const currentTile = board[r][colIndex];
    if (isNormalPiece(currentTile)) {
      board[r][colIndex] = turnAlivePieceDead(currentTile);
      stateChanges.push([r, colIndex, board[r][colIndex]]);
      deadCount++;
    } else if (isBarrelTile(currentTile)) {
      // Damage barrel: Full -> Half, Half -> Empty
      const damagedBarrel = damageBarrel(currentTile);
      if (damagedBarrel !== currentTile) {
        board[r][colIndex] = damagedBarrel;
        stateChanges.push([r, colIndex, board[r][colIndex]]);
        // Count damaged barrels as cleared if they become empty (dead)
        if (isDeadPiece(damagedBarrel)) {
          deadCount++;
        }
      }
    }
  }

  // Mark cells in the same row as dead or damage barrels
  for (let c = 0; c < boardSize; c++) {
    if (c !== colIndex) { // Check to avoid processing the same cell twice
      const currentTile = board[rowIndex][c];
      if (isNormalPiece(currentTile)) {
        board[rowIndex][c] = turnAlivePieceDead(currentTile);
        stateChanges.push([rowIndex, c, board[rowIndex][c]]);
        deadCount++;
      } else if (isBarrelTile(currentTile)) {
        // Damage barrel: Full -> Half, Half -> Empty
        const damagedBarrel = damageBarrel(currentTile);
        if (damagedBarrel !== currentTile) {
          board[rowIndex][c] = damagedBarrel;
          stateChanges.push([rowIndex, c, board[rowIndex][c]]);
          // Count damaged barrels as cleared if they become empty (dead)
          if (isDeadPiece(damagedBarrel)) {
            deadCount++;
          }
        }
      }
    }
  }

  // Mark the original cell (from where the power-up effect is originating) as dead
  board[rowIndex][colIndex] = turnAlivePieceDead(board[rowIndex][colIndex]);
  stateChanges.push([rowIndex, colIndex, board[rowIndex][colIndex]]);
  return [deadCount + 1, stateChanges];  // +1 for the original cell
};

/**
 * Damages adjacent barrel tiles when a match explodes
 */
export const damageAdjacentBarrels = (
  board: GameBoard, 
  matchPositions: Position[], 
  boardSize: number
): StateChange[] => {
  const stateChanges: StateChange[] = [];
  const damagedPositions = new Set<string>();
  
  matchPositions.forEach(({ row, col }) => {
    // Check only 4 adjacent positions (horizontal/vertical only, no diagonals)
    const adjacentOffsets: [number, number][] = [
      [-1, 0], // up
      [0, -1], // left
      [0, 1],  // right
      [1, 0]   // down
    ];
    
    adjacentOffsets.forEach(([rowOffset, colOffset]) => {
      const adjRow = row + rowOffset;
      const adjCol = col + colOffset;
      const posKey = `${adjRow}-${adjCol}`;
      
      // Check if position is valid and not already damaged this round
      if (adjRow >= 0 && adjRow < boardSize && 
          adjCol >= 0 && adjCol < boardSize && 
          !damagedPositions.has(posKey)) {
        
        const currentTile = board[adjRow][adjCol];
        if (isBarrelTile(currentTile)) {
          const damagedTile = damageBarrel(currentTile);
          if (damagedTile !== currentTile) {
            board[adjRow][adjCol] = damagedTile;
            stateChanges.push([adjRow, adjCol, damagedTile]);
            damagedPositions.add(posKey);
          }
        }
      }
    });
  });
  
  return stateChanges;
};